"use client"

import { useState, useRef, useLayoutEffect } from 'react'
import Link from 'next/link'
import OptimizedProductImage from '@/components/products/OptimizedProductImage'

interface OptionColor {
  skuId?: string
  name?: string
  from: string
  to: string
  images?: string[]
}

interface ProductCardMediaProps {
  productUrl: string
  productId: string
  defaultImage: string | null
  alt: string
  optionColors?: OptionColor[] | null
}

/**
 * Small client-side component that renders the product image together with
 * optional colour swatches. Selecting a colour will swap the displayed image
 * (if an image is available for that option) and visually highlight the chosen
 * swatch. The component purposefully keeps its scope narrow so the surrounding
 * product card remains a Server Component, minimising client JS.
 */
export default function ProductCardMedia({
  productUrl,
  productId,
  defaultImage,
  alt,
  optionColors
}: ProductCardMediaProps) {
  const hasColours = Array.isArray(optionColors) && optionColors.length > 0
  const [selectedIdx, setSelectedIdx] = useState<number | null>(hasColours ? 0 : null)
  const swatchRefs = useRef<(HTMLButtonElement | null)[]>([])
  const swatchContainerRef = useRef<HTMLDivElement | null>(null)
  const [pillPos, setPillPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 })

  // Smart sizing and color limiting logic
  const colorCount = optionColors?.length || 0
  const maxColors = colorCount > 9 ? 9 : colorCount
  const displayColors = optionColors?.slice(0, maxColors) || []

  // Dynamic sizing based on color count
  const getColorSize = () => {
    if (colorCount <= 6) return 'w-6 h-6 md:w-7 md:h-7'
    if (colorCount <= 9) return 'w-5 h-5 md:w-6 md:h-6'
    return 'w-5 h-5 md:w-6 md:h-6'
  }

  // Update pill position when selectedIdx or swatches change
  useLayoutEffect(() => {
    if (
      selectedIdx === null ||
      !swatchContainerRef.current ||
      !swatchRefs.current[selectedIdx]
    )
      return
    const containerRect = swatchContainerRef.current.getBoundingClientRect()
    const swatchRect = swatchRefs.current[selectedIdx]!.getBoundingClientRect()
    setPillPos({
      left: swatchRect.left + swatchRect.width / 2 - containerRect.left,
      top: swatchRect.top + swatchRect.height / 2 - containerRect.top,
    })
  }, [selectedIdx, displayColors.length])

  // Update on window resize
  useLayoutEffect(() => {
    function handleResize() {
      if (
        selectedIdx === null ||
        !swatchContainerRef.current ||
        !swatchRefs.current[selectedIdx]
      )
        return
      const containerRect = swatchContainerRef.current.getBoundingClientRect()
      const swatchRect = swatchRefs.current[selectedIdx]!.getBoundingClientRect()
      setPillPos({
        left: swatchRect.left + swatchRect.width / 2 - containerRect.left,
        top: swatchRect.top + swatchRect.height / 2 - containerRect.top,
      })
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [selectedIdx, displayColors.length])

  // Determine which image should currently be displayed
  const currentImage =
    selectedIdx !== null && optionColors?.[selectedIdx]?.images?.length
      ? optionColors[selectedIdx]!.images![0]
      : defaultImage ?? undefined

  return (
    <div className="relative">
      {/* Simplified SVG Filters for Clean Glass Effect */}
      <svg className="absolute inset-0 w-0 h-0 pointer-events-none">
        <defs>
          {/* Clean glass blur effect */}
          <filter id={`glass-blur-${productId}`} x="0%" y="0%" width="100%" height="100%">
            <feGaussianBlur stdDeviation="0.5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1.1 0 0 0 0.02
                      0 1.1 0 0 0.02
                      0 0 1.1 0 0.02
                      0 0 0 1 0"
            />
          </filter>

          {/* Subtle inner glow */}
          <filter id={`inner-glow-${productId}`} x="0%" y="0%" width="100%" height="100%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      <Link href={productUrl} className="block">
        <div className="h-[160px] sm:h-[200px] md:h-auto md:aspect-square relative overflow-hidden">
          {currentImage && (
            <OptimizedProductImage
              src={currentImage}
              alt={alt}
              fill
              priority={false}
              className="object-cover transform group-hover:scale-105 transition-transform duration-700 ease-in-out"
              sizes="(max-width: 640px) 100vw, (max-width: 1200px) 50vw, 33vw"
              showSpaceIcon={true}
              watermarkSize="medium"
            />
          )}
          {/* Decorative overlays preserved from the original design */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 mix-blend-soft-light z-10" />
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bg-main/90 opacity-100 transition-opacity duration-500 ease-in-out" />
          </div>
        </div>
      </Link>

      {hasColours && (
        <div className="absolute inset-x-1 -bottom-[20px] translate-y-2 z-20 pointer-events-auto">
          <div className="relative p-4">
            {/* Clean Liquid Glass Container */}
            <div className="relative overflow-hidden rounded-3xl backdrop-blur-xl bg-transparent border border-white/20 shadow-2xl shadow-black/20">
              {/* Main glass container */}
              <div className="relative p-2 overflow-hidden">
                {/* Clean glass surface */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/11 via-white/6 to-white/3 rounded-3xl" />
                <div className="absolute inset-0 bg-gradient-to-tl from-black/3 via-transparent to-white/3 rounded-3xl" />

                {/* Subtle inner shadow */}
                <div className="absolute inset-0 rounded-3xl shadow-inner shadow-black/10" />

                {/* Color selector grid */}
                <div className="flex justify-around items-center gap-2 relative z-10" ref={swatchContainerRef}>
                  {/* Apple-style glass pill selector */}
                  {selectedIdx !== null && (
                    <div
                      className="absolute rounded-3xl bg-transparent border-0 shadow-2xl shadow-black/40 transition-all duration-700 ease-out z-40 pointer-events-none"
                      style={{
                        left: pillPos.left,
                        top: pillPos.top,
                        transform: 'translate(-50%, -50%) scale(1.05)',
                        transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                        backdropFilter: 'blur(1px) brightness(1.15) contrast(1.1)',
                        WebkitBackdropFilter: 'blur(1px) brightness(1.15) contrast(1.1)',
                        width: getColorSize().includes('w-6') ? '2.7rem' : '3rem',
                        height: getColorSize().includes('w-6') ? '2.2rem' : '2rem',
                      }}
                    >
                      {/* Glass magnification effect */}
                      <div className="absolute inset-0 rounded-3xl"
                        style={{
                          background: 'radial-gradient(ellipse at center, transparent 0%, transparent 60%, rgba(255,255,255,0.03) 80%, rgba(255,255,255,0.08) 95%, rgba(255,255,255,0.15) 100%)',
                        }} />

                      {/* Glass edge refraction - light bending at edges */}
                      <div className="absolute inset-0 rounded-3xl"
                        style={{
                          background: 'radial-gradient(ellipse at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 35%), radial-gradient(ellipse at 75% 75%, rgba(255,255,255,0.15) 0%, transparent 30%)',
                          filter: 'blur(0.5px)',
                        }} />

                      {/* Glass caustics - concentrated light patterns */}
                      <div className="absolute inset-0 rounded-3xl"
                        style={{
                          background: 'conic-gradient(from 0deg at 50% 50%, transparent 0%, rgba(255,255,255,0.08) 12%, transparent 25%, rgba(255,255,255,0.08) 37%, transparent 50%, rgba(255,255,255,0.08) 62%, transparent 75%, rgba(255,255,255,0.08) 87%, transparent 100%)',
                          opacity: 0.7,
                        }} />

                      {/* Glass surface tension and thickness */}
                      <div className="absolute inset-0 rounded-3xl"
                        style={{
                          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), inset 0 -1px 1px rgba(0,0,0,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }} />

                      {/* Chromatic aberration at curved edges */}
                      <div className="absolute inset-0 rounded-3xl"
                        style={{
                          background: 'radial-gradient(ellipse at center, transparent 75%, rgba(0,150,255,0.015) 85%, rgba(255,100,150,0.015) 95%, transparent 100%)',
                        }} />
                    </div>
                  )}

                  {displayColors.map((col, idx) => {
                    const isActive = idx === selectedIdx
                    return (
                      <button
                        type="button"
                        key={col.skuId || col.name || col.from}
                        ref={el => { swatchRefs.current[idx] = el }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedIdx(idx)
                          if (col.name) {
                            window.dispatchEvent(
                              new CustomEvent('productColorSelect', {
                                detail: {
                                  productId,
                                  selectedColor: col.name,
                                },
                              })
                            )
                          }
                        }}
                        aria-pressed={isActive}
                        className={`${getColorSize()} rounded-full relative transition-all duration-300 ease-out focus:outline-none group hover:scale-105 shadow-lg shadow-black/20 z-30`}
                        title={col.name || ''}
                      >

                        {/* Main color with clean glass effect */}
                        <div
                          className="w-full h-full rounded-full relative overflow-hidden border border-white/40 shadow-inner shadow-black/10"
                          style={{
                            background: `linear-gradient(135deg, ${col.from}, ${col.to})`,
                          }}
                        >
                          {/* Clean glass overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/10 rounded-full" />

                          {/* Smooth highlight */}
                          <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white/60 rounded-full blur-sm" />

                          {/* Clean shimmer effect */}
                          <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-full transform transition-transform duration-1000 ${isActive ? 'translate-x-full' : '-translate-x-full group-hover:translate-x-full'
                            }`} />
                        </div>
                      </button>
                    )
                  })}
                </div>

                {/* Clean ambient light */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/5 via-transparent to-black/5 pointer-events-none" />
              </div>

              {/* Outer glow */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 blur-xl opacity-40 -z-10" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 